import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiPhone, FiLogIn, FiEye, FiEyeOff, FiBook } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const font = "'Outfit', sans-serif";
const green = '#27a065';
const darkGreen = '#1a3326';
const lightGreen = '#edfaf4';
const borderGreen = '#c8e6d8';

const Login = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await login(formData.email, formData.password);
            const role = res.user.role;
            if (role === 'student') {
                navigate('/student/dashboard');
            } else if (role === 'teacher') {
                navigate('/teacher/dashboard');
            } else if (role === 'admin' || role === 'superadmin') {
                navigate('/admin/dashboard');
            } else if (role === 'hall_manager') {
                navigate('/hall-manager/dashboard');
            } else if (role === 'exam_controller') {
                navigate('/exam-controller/dashboard');
            } else if (role === 'invigilator') {
                navigate('/invigilator/dashboard');
            } else {
                setError('Access denied. Invalid user role.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid username or password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #d5f3d8 0%, #edfaf4 50%, #f0fff4 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            position: 'relative',
            overflow: 'hidden',
            fontFamily: font,
        }}>
            {/* Background blobs */}
            <div style={{ position: 'absolute', top: -80, left: -80, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, rgba(39,160,101,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(110,219,168,0.22) 0%, transparent 70%)', pointerEvents: 'none' }} />

            <div style={{
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 24,
                padding: '40px 36px',
                width: '100%',
                maxWidth: 420,
                boxShadow: '0 20px 60px rgba(39,160,101,0.14)',
                border: `1.5px solid ${borderGreen}`,
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Brand */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        width: 76, height: 76, margin: '0 auto 14px',
                        background: 'linear-gradient(135deg, #6edba8, #27a065)',
                        borderRadius: 22,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 8px 28px rgba(39,160,101,0.28)',
                    }}>
                        <span style={{ fontSize: 34 }}>🎓</span>
                    </div>
                    <h1 style={{ fontSize: 24, fontWeight: 900, color: darkGreen, margin: '0 0 6px', letterSpacing: -0.5 }}>
                        SVU Login
                    </h1>
                    <p style={{ fontSize: 13, color: '#4a7060', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <FiBook size={13} /> Swami Vivekananda Exam Portal
                    </p>
                </div>

                {/* Error */}
                {error && (
                    <div style={{
                        background: 'rgba(239,68,68,0.07)', border: '1.5px solid rgba(239,68,68,0.25)',
                        borderRadius: 12, padding: '11px 14px', marginBottom: 18,
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 13, color: '#c05050', fontFamily: font,
                    }}>
                        <span style={{ fontSize: 16 }}>⚠️</span> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Student ID */}
                    <div style={{ marginBottom: 18 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#4a7060', marginBottom: 7, textTransform: 'uppercase', letterSpacing: 0.7 }}>
                            Username
                        </label>
                        <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: green, display: 'flex', fontSize: 16 }}>
                                <FiUser />
                            </span>
                            <input
                                type="text"
                                placeholder="Enter your username"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                style={{
                                    width: '100%', boxSizing: 'border-box',
                                    padding: '12px 14px 12px 42px',
                                    borderRadius: 12, border: `1.5px solid ${borderGreen}`,
                                    background: lightGreen, fontSize: 14, fontFamily: font,
                                    color: darkGreen, outline: 'none',
                                    transition: 'border-color 0.2s ease',
                                }}
                                onFocus={e => e.target.style.borderColor = green}
                                onBlur={e => e.target.style.borderColor = borderGreen}
                            />
                        </div>
                    </div>

                    {/* Phone Number (password) */}
                    <div style={{ marginBottom: 26 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#4a7060', marginBottom: 7, textTransform: 'uppercase', letterSpacing: 0.7 }}>
                            Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: green, display: 'flex', fontSize: 16 }}>
                                <FiPhone />
                            </span>
                            <input
                                type={showPass ? 'text' : 'password'}
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required
                                style={{
                                    width: '100%', boxSizing: 'border-box',
                                    padding: '12px 42px 12px 42px',
                                    borderRadius: 12, border: `1.5px solid ${borderGreen}`,
                                    background: lightGreen, fontSize: 14, fontFamily: font,
                                    color: darkGreen, outline: 'none',
                                    transition: 'border-color 0.2s ease',
                                }}
                                onFocus={e => e.target.style.borderColor = green}
                                onBlur={e => e.target.style.borderColor = borderGreen}
                            />
                            <span onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#8ab5a0', display: 'flex', fontSize: 18 }}>
                                {showPass ? <FiEyeOff /> : <FiEye />}
                            </span>
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%', padding: '13px',
                            borderRadius: 13, border: 'none',
                            background: loading ? '#a8dfc0' : 'linear-gradient(135deg, #6edba8, #27a065)',
                            color: '#fff', fontSize: 15, fontWeight: 800,
                            fontFamily: font, cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            boxShadow: '0 6px 20px rgba(39,160,101,0.30)',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <FiLogIn size={17} />
                        {loading ? 'Signing in…' : 'Sign In'}
                    </button>
                </form>

                {/* Info note */}
                <div style={{ marginTop: 24, padding: '12px 16px', background: lightGreen, borderRadius: 12, border: `1px solid ${borderGreen}` }}>
                    <p style={{ fontSize: 12, color: '#4a7060', margin: 0, lineHeight: 1.6, fontFamily: font }}>
                        <strong>🔐 How to login:</strong><br />
                        Use the <strong>username</strong> and <strong>password</strong> provided by your institution.<br />
                        Contact admin if you have not received your credentials.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
